import { createSelector, createSlice } from '@reduxjs/toolkit';

import type {
  AdUnit,
  ListAdUnitsResponse,
} from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import { type RootState } from '@app/settings-store';
import { type FetchStatus } from '@app/store/settings/types';
import { fetchAdUnitByCompId, fetchAdUnitsList } from './fetch-actions';

type AdsenseAdunitsState = {
  adUnits?: AdUnit[];
  connectedAdUnit?: AdUnit;
  nextPageToken?: ListAdUnitsResponse['nextPageToken'];
  fullDataFetched: boolean;
  status: FetchStatus;
  connectedAdUnitStatus: FetchStatus;
};

const adsenseAdunitsState: AdsenseAdunitsState = {
  adUnits: [],
  nextPageToken: '',
  status: 'idle',
  connectedAdUnitStatus: 'idle',
  fullDataFetched: false,
};

export const adsenseAdunitsSlice = createSlice({
  name: 'adsenseAdunits',
  initialState: adsenseAdunitsState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(fetchAdUnitsList.fulfilled, (state, action) => {
        const { adUnits, nextPageToken } = action.payload;
        /** @NOTE - connected adUnit always first in list */
        state.adUnits = [
          ...(state.adUnits || []),
          ...(adUnits?.filter(
            (adUnit) =>
              adUnit.reportingDimensionId !==
              state.connectedAdUnit?.reportingDimensionId,
          ) || []),
        ];
        state.fullDataFetched = !nextPageToken;
        state.nextPageToken = nextPageToken;
        state.status = 'success';
      })
      .addCase(fetchAdUnitsList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAdUnitsList.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(fetchAdUnitByCompId.fulfilled, (state, action) => {
        if (action.payload.adUnit) {
          /** @NOTE - connected adUnit always first in list */
          state.adUnits = [action.payload.adUnit, ...(state.adUnits || [])];
        }
        state.connectedAdUnit = action.payload.adUnit;
        state.connectedAdUnitStatus = 'success';
      })
      .addCase(fetchAdUnitByCompId.pending, (state) => {
        state.connectedAdUnitStatus = 'loading';
      })
      .addCase(fetchAdUnitByCompId.rejected, (state) => {
        state.connectedAdUnitStatus = 'error';
      });
  },
});

export const selectAdsenseAdunitState = (state: RootState) =>
  state.adsenseAdunits;

export const selectAdUnitsData = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.adUnits || [],
);
export const selectConnectedAdUnit = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.connectedAdUnit,
);
export const selectAdUnitsStatus = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.status,
);
export const selectConnectedAdUnitStatus = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.connectedAdUnitStatus,
);
export const selectNextPageToken = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.nextPageToken,
);
export const selectFullDataFetched = createSelector(
  [selectAdsenseAdunitState],
  (state) => state.fullDataFetched,
);

export const {} = adsenseAdunitsSlice.actions;

export default adsenseAdunitsSlice.reducer;
export * from './fetch-actions';
